import { useEffect, useState } from "react"
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Backdrop,
  CircularProgress,
  useTheme,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { collection, doc, setDoc, getDoc, getDocs } from "firebase/firestore"
import { db } from "../../firebase"
import { toast } from 'react-toastify'
import dayjs from "dayjs"
import { tokens } from "../../theme"
import { useNavigate, useParams } from "react-router-dom"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { parseWeekId, getIsoWeekDateRange } from "../../utils/isoWeek"
import { IWeeklyHygieneManagement, formatWeekRangeLabel } from "./lib/weeklyHygieneManagement"
import { IBusinessHoliday, isRangeFullyHoliday } from "../../utils/businessHolidays"

// 管理項目の定義
const CHECK_CATEGORIES = [
  {
    key: "ceilingChecks",
    title: "天井、内壁、照明設備、換気設備の清掃状態",
    items: [
      "１．ホコリや汚れが残ってないか",
      "２．カビやサビ、こびりついた汚れ、結露はないか",
      "３．破損、故障はないか",
      "４．換気空調設備のフィルターは汚れていないか"
    ]
  },
  {
    key: "doorChecks",
    title: "窓、出入り口（人､物）の状態",
    items: [
      "１．昆虫の防除設備がない場合、窓等を開放していないか",
      "２．従事者以外の者が施設内に立ち入っていないか"
    ]
  },
  {
    key: "equipmentMaintenanceChecks",
    title: "機械器具部品の定期交換、メンテナンスに関すること",
    items: [
      "１．部品の交換は適切であるか",
      "２．正常に作動するか",
      "３．欠落した部品はないか"
    ]
  },
  {
    key: "storageChecks",
    title: "冷凍・冷蔵庫（室）内での保管状態",
    items: [
      "１．原材料と中間製品、完成品は、相互汚染を防止するため、区分けして保管されているか",
      "２．原材料は、相互汚染を防止するため、食品別に区分して保管されているか",
      "３．先入れ先出しを徹底しているか（期限切れの食品はないか）"
    ]
  }
]

const WeeklyHygieneManagementForm = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const navigate = useNavigate()
  const { weekId } = useParams<{ weekId: string }>()

  const [processing, setProcessing] = useState<boolean>(true)
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [createdAt, setCreatedAt] = useState<string | undefined>(undefined)
  const [year, setYear] = useState<string>("")
  const [weekOfYear, setWeekOfYear] = useState<number>(0)
  const [weekStartDate, setWeekStartDate] = useState<string>("")
  const [weekEndDate, setWeekEndDate] = useState<string>("")
  const [manager, setManager] = useState<string>("")
  const [supervisor, setSupervisor] = useState<string>("")
  const [ceilingChecks, setCeilingChecks] = useState<Record<string, string>>({})
  const [doorChecks, setDoorChecks] = useState<Record<string, string>>({})
  const [equipmentMaintenanceChecks, setEquipmentMaintenanceChecks] = useState<Record<string, string>>({})
  const [storageChecks, setStorageChecks] = useState<Record<string, string>>({})
  const [verifier, setVerifier] = useState<string>("")
  const [remarks, setRemarks] = useState<string>("")

  useEffect(() => {
    if (!weekId) return
    const parsed = parseWeekId(weekId)
    if (!parsed) {
      toast.error("不正な週の指定です")
      navigate('/admin/weekly-hygiene-management')
      return
    }

    const { isoYear, isoWeek } = parsed
    const { start, end } = getIsoWeekDateRange(isoYear, isoWeek)
    setYear(isoYear.toString())
    setWeekOfYear(isoWeek)
    setWeekStartDate(start)
    setWeekEndDate(end)

    loadRecord(weekId)
  }, [weekId])

  const initializeChecks = () => {
    const initState: Record<string, string> = {}
    CHECK_CATEGORIES.forEach(category => {
      category.items.forEach((_, index) => {
        initState[`item${index}`] = ""
      })
    })

    setCeilingChecks({ ...initState })
    setDoorChecks({ ...initState })
    setEquipmentMaintenanceChecks({ ...initState })
    setStorageChecks({ ...initState })
  }

  const loadRecord = async (id: string) => {
    setProcessing(true)
    try {
      const docRef = doc(db, "weeklyHygieneManagements", id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const record = docSnap.data() as IWeeklyHygieneManagement

        setIsEditMode(true)
        setCreatedAt(record.createdAt)
        setManager(record.manager || "")
        setSupervisor(record.supervisor || "")
        setCeilingChecks(record.ceilingChecks || {})
        setDoorChecks(record.doorChecks || {})
        setEquipmentMaintenanceChecks(record.equipmentMaintenanceChecks || {})
        setStorageChecks(record.storageChecks || {})
        setVerifier(record.verifier || "")
        setRemarks(record.remarks || "")
      } else {
        // 休業日マスタでこの週の全日が休業に該当するか確認
        const parsed = parseWeekId(id)
        if (parsed) {
          const { start, end } = getIsoWeekDateRange(parsed.isoYear, parsed.isoWeek)
          const holidaysSnapshot = await getDocs(collection(db, "businessHolidays"))
          const holidays = holidaysSnapshot.docs.map(d => ({ id: d.id, ...d.data() } as IBusinessHoliday))

          if (isRangeFullyHoliday(start, end, holidays)) {
            toast.info("この週は休業期間として登録されています。一覧画面に戻ります。")
            navigate('/admin/weekly-hygiene-management', { replace: true })
            return
          }
        }

        setIsEditMode(false)
        initializeChecks()
      }
    } catch (e) {
      toast.error("データの読み込みに失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const getCheckState = (category: string): [Record<string, string>, React.Dispatch<React.SetStateAction<Record<string, string>>>] => {
    switch (category) {
      case "ceilingChecks": return [ceilingChecks, setCeilingChecks]
      case "doorChecks": return [doorChecks, setDoorChecks]
      case "equipmentMaintenanceChecks": return [equipmentMaintenanceChecks, setEquipmentMaintenanceChecks]
      case "storageChecks": return [storageChecks, setStorageChecks]
      default: return [{}, () => {}]
    }
  }

  const handleCheckChange = (category: string, itemKey: string, value: string) => {
    const [state, setState] = getCheckState(category)
    setState({
      ...state,
      [itemKey]: value
    })
  }

  const saveData = async () => {
    if (!weekId) return

    if (!manager) {
      toast.error("衛生管理者を入力してください")
      return
    }

    if (!supervisor) {
      toast.error("担当責任者を入力してください")
      return
    }

    if (!verifier) {
      toast.error("確認者を入力してください")
      return
    }

    setProcessing(true)
    const now = dayjs().format("YYYY-MM-DD HH:mm:ss")
    const data: IWeeklyHygieneManagement = {
      year,
      weekOfYear,
      weekStartDate,
      weekEndDate,
      manager,
      supervisor,
      ceilingChecks,
      doorChecks,
      equipmentMaintenanceChecks,
      storageChecks,
      verifier,
      remarks,
      createdAt: createdAt || now,
      updatedAt: now
    }

    try {
      await setDoc(doc(collection(db, "weeklyHygieneManagements"), weekId), data)
      toast.success(isEditMode ? "週次衛生管理点検票を更新しました" : "週次衛生管理点検票を保存しました")
      navigate('/admin/weekly-hygiene-management')
    } catch (e) {
      toast.error("保存に失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const handleBack = () => {
    navigate('/admin/weekly-hygiene-management')
  }

  return (
    <Box m={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          戻る
        </Button>
        <Typography variant="h4" fontWeight="bold">
          {isEditMode ? '週次衛生管理点検票 編集' : '週次衛生管理点検票 新規作成'}
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" mb={2}>
          基本情報
        </Typography>

        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} mb={3}>
          <TextField
            label="対象週"
            value={weekOfYear ? `${year}年 第${weekOfYear}週（${formatWeekRangeLabel(weekStartDate, weekEndDate)}）` : ""}
            disabled
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="衛生管理者"
            value={manager}
            onChange={(e) => setManager(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="担当責任者"
            value={supervisor}
            onChange={(e) => setSupervisor(e.target.value)}
            fullWidth
            required
          />
        </Box>

        <Typography variant="body2" color="textSecondary" mb={3}>
          適の場合は○、不適の場合は×を記入し、措置を特記事項に記入してください。
        </Typography>
      </Paper>

      {CHECK_CATEGORIES.map((category) => {
        const [checkState] = getCheckState(category.key)

        return (
          <Paper key={category.key} elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" mb={2} sx={{ backgroundColor: colors.primary[400], p: 1 }}>
              {category.title}
            </Typography>
            <Typography variant="body2" fontWeight="bold" mb={2}>
              管理事項:
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
                    <TableCell sx={{ fontWeight: "bold", width: "70%" }}>項目</TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "30%", textAlign: "center" }}>チェック</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {category.items.map((item, index) => {
                    const itemKey = `item${index}`
                    const value = (checkState as Record<string, string>)[itemKey] || ""

                    return (
                      <TableRow key={index} hover>
                        <TableCell>{item}</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={value === "○"}
                                onChange={(e) => handleCheckChange(category.key, itemKey, e.target.checked ? "○" : "×")}
                                color="success"
                              />
                            }
                            label="○"
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )
      })}

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2} sx={{ backgroundColor: colors.primary[400], p: 1 }}>
          確認者
        </Typography>
        <TextField
          value={verifier}
          onChange={(e) => setVerifier(e.target.value)}
          fullWidth
          placeholder="確認者名を入力"
          required
          sx={{ mb: 3 }}
        />

        <Typography variant="h6" fontWeight="bold" mb={2}>
          特記事項
        </Typography>
        <TextField
          multiline
          rows={6}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          fullWidth
          placeholder="×の場合は、措置結果をここに記載すること"
        />

        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={handleBack}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={saveData}
            disabled={processing}
          >
            {isEditMode ? "更新" : "保存"}
          </Button>
        </Box>
      </Paper>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={processing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}

export default WeeklyHygieneManagementForm
